<template>
  <div class="bb-payment">
    <transition name="fade" appear>
      <loader-fly v-if="loaderRun"></loader-fly>
    </transition>
    <h2 class="bb-payment-header">Receive payment</h2>
    <div class="bb-payment-info">
      <div class="bb-payment-info-form bb-form">
        <div class="bb-payment-info-form-item">
          <label class="bb-form__label">Customer</label>
          <v-select class="bb-payment-info-form-item__select"
                    label="nickName"
                    :options="sortedContactsList"
                    :clearable="false"
                    @search="searchCustomer"
                    @input="setCustomer"/>
        </div>
        <div class="bb-payment-info-form-item">
          <label class="bb-form__label">Amount Received</label>
          <input class="bb-payment-info-form-item__input bb-input bb-input_border"
                 :class="{'bb-input_error': errorAmount}"
                 type="text"
                 v-autofocus="errorAmount"
                 @focus="focusAmount"
                 @input="checkAmount()"
                 @keyup.enter="beforeCreatePayment()"
                 v-model="payment.amountReceived"/>
          <span v-if="errorAmount" class="bb-form-item__error">{{errorAmount}}</span>
        </div>
        <div class="bb-payment-info-form-item">
          <label class="bb-form__label">Payment Date</label>
          <DataPicker @setDate="setPaymentDate"
                      :no-future="true"
                      :no-past="checkedOverpaidInv.length > 0"
                      :disable="true"
                      :default-date="payment.paymentDate"/>
        </div>
      </div>
      <div class="bb-payment-info-total">
        <div class="bb-payment-info-total__title">Total received</div>
        <div class="bb-payment-info-total__received">
          <span>AED</span>
          {{convertNumber(amountReceivedStr)}}
        </div>
        <div class="bb-payment-info-total-paid bb-payment-info-total-paid__underpaid">
          <span class="bb-payment-info-total-paid__title">Underpaid</span>
          <span class="bb-payment-info-total-paid__number">{{totalPaid.underpaid}}</span>
        </div>
        <div class="bb-payment-info-total-paid bb-payment-info-total-paid__overpaid">
          <span class="bb-payment-info-total-paid__title">Overpaid</span>
          <span class="bb-payment-info-total-paid__number">{{totalPaid.overpaid}}</span>
        </div>
      </div>
    </div>
    <div class="bb-payment-invoice">
      <h3 class="bb-payment-invoice__title">Outstanding</h3>
      <vue-perfect-scrollbar class="bb-payment-invoice-table-wrapper">
        <table class="bb-table bb-table_modal">
          <thead class="bb-table-header">
          <tr class="bb-table-header-row">
            <th class="bb-table-header__item bb-table-header__item_radio"></th>
            <th class="bb-table-header__item">
              Description
            </th>
            <th class="bb-table-header__item">
              Original Amount, AED
            </th>
            <th class="bb-table-header__item">
              Open Balance, AED
            </th>
            <th class="bb-table-header__item">
              Payment, AED
            </th>
            <th></th>
          </tr>
          </thead>
          <tbody class="bb-table-body" name="fade-form" is="transition-group">
          <tr class="bb-table-body-row bb-table-body-row_check-list bb-table-body-row_payment"
              v-for="(invoice, index) in invoicesClientUnderpaid"
              :key="invoice.id"
              @click="checkedWillPay(false, index, invoice)">
            <td class="bb-table-body__item">
              <div class="bb-table-body__item_radio">
                <checkbox
                  :name="index"
                  :section="invoice"
                  @checkboxChange="checkedWillPay"
                  :value="checkedUnderpaidInv.indexOf(invoice) !== -1"
                />
              </div>
            </td>
            <td class="bb-table-body__item">
              Invoice {{invoice.number}}
            </td>
            <td class="bb-table-body__item">
              {{convertNumber(invoice.totalInvoicedAed)}}
            </td>
            <td class="bb-table-body__item">
              {{convertNumber(invoice.totalInvoicedAed - invoice.received)}}
            </td>
            <td class="bb-table-body__item">
              <span v-if="checkedUnderpaidInv.indexOf(invoice) !== -1">
                {{getCheckedUnderPaid(invoice)}}
              </span>
              <span v-else>0.00</span>
            </td>
          </tr>
          </tbody>
        </table>
      </vue-perfect-scrollbar>
    </div>
    <transition name="fade-form">
      <div class="bb-payment-invoice" v-if="invoicesClientOverpaid.length > 0">
        <h3 class="bb-payment-invoice__title">Credit</h3>
        <vue-perfect-scrollbar class="bb-payment-invoice-table-wrapper">
          <table class="bb-table bb-table_modal">
            <thead class="bb-table-header">
            <tr class="bb-table-header-row">
              <th class="bb-table-header__item bb-table-header__item_radio"></th>
              <th class="bb-table-header__item">
                Description
              </th>
              <th class="bb-table-header__item
                         bb-payment-invoice-table-wrapper_width"
              >
                Original Amount, AED
              </th>
              <th class="bb-table-header__item bb-payment-invoice-table-wrapper_width">
                Open Balance, AED
              </th>
              <th class="bb-table-header__item bb-payment-invoice-table-wrapper_payment"
              >
                Payment, AED
              </th>
              <th></th>
            </tr>
            </thead>
            <tbody class="bb-table-body">
            <tr class="bb-table-body-row bb-table-body-row_check-list bb-table-body-row_payment"
                v-for="(invoice, index) in invoicesClientOverpaid"
                :key="invoice.id"
                @click="checkedCredit(false, index, invoice)"
                @mouseleave="closePopup(invoice)"
            >
              <td class="bb-table-body__item">
                <checkbox
                  :name="index"
                  :section="invoice"
                  :disabled="checkRefundStatus(invoice.$statusRefund)"
                  @checkboxChange="checkedCredit"
                  :value="checkedOverpaidInv.indexOf(invoice) !== -1"
                />
              </td>
              <td class="bb-table-body__item">
                <span>{{invoice.$title}} {{invoice.number}}</span>
                <span class="bb-payment-draft"
                      v-if="invoice.$statusRefund === 2"
                      :ref="`refunded${invoice.id}`"
                >
                  To be refunded
                </span>
                <transition name="fade" mode="out-in">
                  <div v-if="invoice.$statusRefund === 2 && invoice.$openPopup"
                       class="bb-table-body-popup"
                       :style="popupPosition"
                  >
                    Delete refund request to use Tax Credit Note
                  </div>
                </transition>
              </td>
              <td class="bb-table-body__item">
                {{convertNumber(invoice.totalInvoicedAed || invoice.balance)}}
              </td>
              <td class="bb-table-body__item">
                {{convertNumber((invoice.received - invoice.totalInvoicedAed) || invoice.balance)}}
              </td>
              <td class="bb-table-body__item">
                <transition name="fade-form">
                  <template v-if="checkedOverpaidInv.indexOf(invoice) !== -1">
                    <input
                      @click.stop
                      class="bb-input bb-input_border"
                      v-autofocus
                      @focus="focusCredit(invoice, index)"
                      @change="changeCredit(invoice, index)"
                      @input="inputCredit(invoice, index)"
                      @keyup.enter="beforeCreatePayment()"
                      v-model="invoice.credit">
                  </template>
                  <template v-if="checkedOverpaidInv.indexOf(invoice) === -1">
                    <div>0.00</div>
                  </template>
                </transition>
              </td>
            </tr>
            </tbody>
          </table>
        </vue-perfect-scrollbar>
      </div>
    </transition>
    <transition name="fade-form">
      <div class="bb-payment-invoice-footer">
        <span class="bb-payment-invoice-footer__error">{{errorText}}</span>
        <button class="bb-button bb-payment-invoice-footer__button"
                :disabled="!userInfo.id"
                v-if="checkedUnderpaidInv.length > 0"
                @click="beforeCreatePayment()">
          SAVE
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import CustomSelect from '@/components/Main/Inputs/CustomSelect'
import DataPicker from '@/components/Main/DataPicker/DataPicker'
import ButtonsEdit from '@/components/Main/ButtonsEdit/ButtonsEdit'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import radio from '@/components/Main/Inputs/radio'
import checkbox from '@/components/Main/Inputs/checkbox'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {statusRefundList} from '@/constants/refundStatusList'
import ContactsApi from '@/api/Contacts/api'
import InvoicesApi from '@/api/Invoce/api'
import PaymentApi from '@/api/Payment/api'
import SoaCreditNote from '@/api/SoaCreditNote/api'
import convertNumber from '@/mixin/convertNumber'
import _ from 'lodash'
import VueCookie from 'vue-cookie'
import userApi from '@/api/User/api'
import vSelect from 'vue-select'

export default {
  name: 'Payment',
  mixins: [convertNumber],
  components: {
    CustomSelect,
    DataPicker,
    ButtonsEdit,
    LoaderFly,
    radio,
    checkbox,
    VuePerfectScrollbar,
    vSelect
  },
  data: function () {
    return {
      errorText: '',
      errorAmount: '',
      loaderRun: false,
      popupClick: false,
      totalOverpaid: 0,
      payment: {
        amountReceived: 0,
        customer: '',
        paymentDate: new Date()
      },
      checkedPayments: {},
      calculatedOverpaidInvoices: {},
      popupPosition: {},
      checkedUnderpaidInv: [],
      sortedContactsList: [],
      checkedOverpaidInv: [],
      activeInvoice: 0,
      refundStatusList: statusRefundList,
      invoicesClientOverpaid: [],
      invoicesClientUnderpaid: []
    }
  },
  computed: {
    invoicesList: {
      get: function () {
        return this.$store.getters.invoicesList
      },
      set: function (data) {
        this.$store.dispatch('setInvoicesList', data)
      }
    },
    contactsList: {
      get: function () {
        return this.$store.getters.contactsList
      },
      set: function (data) {
        this.$store.dispatch('setContactsList', data)
      }
    },
    invoicesTotal: {
      get: function () {
        return this.$store.getters.invoicesTotal
      },
      set: function (data) {
        this.$store.dispatch('setInvoicesTotal', data)
      }
    },
    userInfo: {
      get: function () {
        return this.$store.getters.userInfo
      },
      set: function (data) {
        this.$store.dispatch('setUserInfo', data)
      }
    },
    totalAmount () {
      return this.amountReceivedStr + this.totalOverpaid
    },
    amountReceivedStr () {
      let amountReceive = this.payment.amountReceived
      if (typeof amountReceive === 'string') {
        amountReceive = +amountReceive.replace(',', '')
      }
      if (isNaN(amountReceive)) {
        return 0
      }
      return +amountReceive
    },
    totalPaid () {
      let balance = 0
      if (this.checkedUnderpaidInv.length > 0) {
        const total = _.reduce(this.checkedUnderpaidInv, (sum, item) => {
          const underpaid = +item.totalInvoicedAed - +item.received
          return underpaid ? sum + underpaid : sum + 0
        }, 0)
        const received = this.totalAmount
        balance = received - total
      }
      let overpaid
      let underpaid
      if (balance > 0) {
        overpaid = balance
        underpaid = 0
      } else if (balance < 0) {
        underpaid = balance
        overpaid = 0
      } else {
        underpaid = 0
        overpaid = 0
      }
      return {
        underpaid: this.convertNumber(Math.abs(underpaid)),
        overpaid: this.convertNumber(overpaid)
      }
    }
  },
  methods: {
    roundNum (num) {
      return Math.round(num * 100) / 100
    },
    focusCredit (item, index) {
      if (!item.credit) {
        item.credit = ''
        this.$set(this.invoicesClientOverpaid, index, item)
      }
    },
    getRefundStatus (status) {
      if (!status) return
      const refundStatus = this.refundStatusList.find(item => item.id === status)
      return refundStatus ? refundStatus.name : ''
    },
    checkRefundStatus (status) {
      return status === 2
    },
    inputCredit (item, index) {
      const maxCredit = item.received - item.totalInvoicedAed
      let str = item.credit
      if (typeof str === 'string') {
        str = item.credit.replace(/^0+|[^.\d;]/g, '')
      }
      const num = +str
      if (num > maxCredit) {
        item.credit = this.roundNum(maxCredit)
      }
      this.$set(this.invoicesClientOverpaid, index, item)
      const overpaid = Math.round(this.addOverpaid() * 100) / 100
      this.amountDistribution(overpaid)
      this.checkedOverpaidInv.forEach(item => {
        this.amountOverpaid(item, item.credit)
      })
    },
    sortContacts () {
      this.sortedContactsList = this.contactsList.sort((firstObject, secondObject) => {
        const textA = firstObject.nickName.toUpperCase()
        const textB = secondObject.nickName.toUpperCase()
        return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
      })
    },
    changeCredit (item, index) {
      const maxCredit = item.received - item.totalInvoicedAed
      let str = item.credit
      if (typeof str === 'string') {
        str = item.credit.replace(/^0+|[^.\d;]/g, '')
      }
      const num = +str
      if (num > maxCredit) {
        item.credit = maxCredit
      } else if (num <= 0 || isNaN(num)) {
        item.credit = 0
      } else {
        item.credit = num
      }
      item.credit = this.roundNum(item.credit)
      this.$set(this.invoicesClientOverpaid, index, item)
    },
    setPositionPopup (item) {
      const [ref] = _.get(this.$refs, `refunded${item.id}`, [])
      if (ref) {
        const box = ref.getBoundingClientRect()
        const popups = ref
        const popupWidth = _.get(popups[0], 'offsetWidth', 0)
        const diffLeft = popupWidth / 2
        this.popupPosition = {
          left: (box.left - diffLeft + 5) + 'px',
          top: box.top + 30 + 'px'
        }
      }
    },
    getCheckedUnderPaid (invoice) {
      const checkedInvoice = _.find(this.checkedUnderpaidInv, invoice)
      return checkedInvoice ? this.convertNumber(checkedInvoice.payment) : 0.00
    },
    amountOverpaid (invoice, total, create) {
      let sum = typeof total === 'string' ? total.replace(/^0+|[^.\d;]/g, '') : total
      let credit = 0
      this.$set(this.calculatedOverpaidInvoices, invoice.id, {})
      const length = this.checkedUnderpaidInv.length - 1
      this.checkedUnderpaidInv.forEach((item, index) => {
        let openBalance = item.totalInvoicedAed - item.received
        const balance = _.reduce(this.calculatedOverpaidInvoices, (sum, elem) => {
          sum += +elem[item.id] || 0
          return sum
        }, 0)
        if (create) {
          openBalance = this.checkedPayments[item.id] - balance
        }
        const deference = sum - openBalance
        if (openBalance === 0) {
          credit = 0
        } else if (deference > 0) {
          credit = length === index ? sum : openBalance
          sum = length === index ? 0 : deference
        } else if (deference <= 0) {
          credit = sum
          sum = 0
        }
        this.$set(this.calculatedOverpaidInvoices[invoice.id], item.id, credit)
      })
    },
    amountDistribution (overpaid = 0, create) {
      let sum = this.amountReceivedStr + overpaid
      const length = this.checkedUnderpaidInv.length - 1
      _.map(this.checkedUnderpaidInv, (item, index) => {
        let openBalance = item.totalInvoicedAed - item.received
        let payment
        if (create) {
          openBalance -= this.checkedPayments[item.id] || 0
        }
        const deference = sum - openBalance
        if (deference > 0 && openBalance !== 0) {
          payment = length === index ? sum : openBalance
          item.payment = create ? item.payment : payment
          sum = length === index ? 0 : deference
        } else if (openBalance < 0 && length !== index) {
          return
        } else {
          payment = sum
          item.payment = create ? item.payment : payment
          sum = 0
        }
        if (create) {
          this.checkedPayments[item.id] = item.payment
        }
      })
    },
    checkedWillPay (data, index, item) {
      const indexInChecked = this.checkedUnderpaidInv.indexOf(item)
      if (indexInChecked < 0) {
        this.checkedUnderpaidInv.push(item)
      } else {
        this.checkedUnderpaidInv.splice(indexInChecked, 1)
      }
      const overpaid = Math.round(this.addOverpaid() * 100) / 100
      this.amountDistribution(overpaid)
    },
    closePopup (item) {
      const refundStatus = _.get(item, '$statusRefund', 0)
      if (refundStatus === 2) {
        this.popupClick = false
        this.$set(item, '$openPopup', this.popupClick)
      }
    },
    checkedCredit (data, index, item) {
      const indexInChecked = this.checkedOverpaidInv.indexOf(item)
      const refundStatus = _.get(item, '$statusRefund', 0)
      if (refundStatus === 2) {
        this.popupClick = !this.popupClick
        this.setPositionPopup(item)
        this.$set(item, '$openPopup', this.popupClick)
        return
      }
      if (indexInChecked < 0) {
        this.checkedOverpaidInv.push(item)
      } else {
        this.checkedOverpaidInv.splice(indexInChecked, 1)
      }
      const overpaid = Math.round(this.addOverpaid() * 100) / 100
      this.amountDistribution(overpaid)
    },
    validateAmount (amount) {
      return +String(amount).replace(/^0+|[^.\d;]/g, '')
    },
    focusAmount () {
      this.errorAmount = ''
      if (this.payment.amountReceived === 0) {
        this.payment.amountReceived = ''
      }
    },
    checkAmount () {
      if (this.payment.amountReceived === 0) {
        return
      }
      if (+this.payment.amountReceived < 0) {
        this.payment.amountReceived = Math.abs(+this.payment.amountReceived)
      }
      const overpaid = Math.round(this.addOverpaid() * 100) / 100
      this.amountDistribution(overpaid)
    },
    setPaymentDate (date) {
      this.payment.paymentDate = date
    },
    setCustomer (item) {
      this.clearProps()
      this.payment.customer = item.id
      this.getInvoices({query: 'isDelete[]=false&client[]=' + item.id})
    },
    searchCustomer (word) {
      if (!word) {
        this.contactsList = _.orderBy(this.contactsList, ['nickName'], ['asc'])
      } else {
        const reg = new RegExp('^' + word, 'gmi')
        this.contactsList = this.contactsList.sort((a, b) => {
          return reg.test(b.nickName) - reg.test(a.nickName)
        })
      }
    },
    async getClientsSelect () {
      const body = {
        contactType: 'contactType[]=1&contactType[]=2'
      }
      try {
        const { data } = await ContactsApi.getClientsSelect(body)
        this.contactsList = data
      } catch (err) {
        console.error(err)
      }
    },
    async getInvoices (query) {
      this.loaderRun = true
      const overpaidList = []
      const underpaidList = []
      const creditNoteList = []
      const invoiceData = await this.getInvoice(query)
      const creditNotesData = await this.getCreditNotes()
      _.forEach(invoiceData.invoices, item => {
        if (item.status === 3) {
          this.$set(item, '$title', 'invoice')
          overpaidList.push(item)
        } else if (item.status === 1 || item.status === 0) {
          underpaidList.push(item)
        }
      })
      _.forEach(creditNotesData.soacreditnotes, item => {
        this.$set(item, '$title', 'Credit Note')
        this.$set(item, 'totalInvoicedAed', 0)
        this.$set(item, 'received', item.balance)
        this.$set(item, 'isCreditNote', true)
        this.$set(item, '$statusRefund', item.status)
        item.id = item.id || item._id
        creditNoteList.push(item)
      })
      const creditNotesFilter = creditNoteList.filter(item => item.$statusRefund !== 3)
      const filterUnderpaidList = _.filter(overpaidList, item => !item.isSoaCreditNote)
      this.invoicesClientOverpaid = filterUnderpaidList.concat(creditNotesFilter)
      this.invoicesClientUnderpaid = underpaidList
      this.loaderRun = false
    },
    async getInvoice (query) {
      try {
        const {data} = await InvoicesApi.getInvoices(query)
        return data
      } catch (e) {
        console.error(e)
        return {}
      }
    },
    async getCreditNotes () {
      try {
        const {data} = await SoaCreditNote.getSoaCreditNotes({client: this.payment.customer})
        return data
      } catch (e) {
        console.error(e)
      }
    },
    getInvoiceById (id) {
      const index = _.findIndex(this.invoicesList.invoices, {id: id})
      InvoicesApi.getInvoiceById(id)
        .then((resp) => {
          this.invoicesList.invoices.splice(index, 1, resp.data)
        })
        .catch((err) => {
          this.loaderRun = false
          console.error(err)
        })
    },
    addOverpaid () {
      if (this.checkedUnderpaidInv.length === 0) { return }
      const overpaid = _.reduce(this.checkedOverpaidInv, (sum, item) => {
        const credit = +String(item.credit).replace(/^0+|[^.\d;]/g, '')
        return credit ? sum + credit : sum + 0
      }, 0)
      this.totalOverpaid = Math.round((overpaid) * 100) / 100
      return Math.round((overpaid) * 100) / 100
    },
    getInvoicesTotal () {
      InvoicesApi.getInvoicesTotal({})
        .then((resp) => {
          this.invoicesTotal = resp.data
        })
        .catch((err) => {
          console.error(err)
        })
    },
    generatePayment (invoice) {
      return {
        number: invoice.number || '',
        company: VueCookie.get('company') || '',
        customer: this.payment.customer || '',
        user: this.userInfo.id || '',
        invoice: invoice.id || '',
        amountReceived: this.roundNum(this.validateAmount(invoice.payment))
      }
    },
    generateOverpaidPayment (invoice) {
      return {
        invoiceUnderId: invoice.id,
        paymentDate: this.payment.paymentDate,
        debit: this.roundNum(this.validateAmount(invoice.payment)),
        overpaidInvoices: [],
        soaCreditNotes: []
      }
    },
    async beforeCreatePayment () {
      const overpaidPaymentFull = {
        company: '',
        customer: this.payment.customer,
        user: this.userInfo.id,
        invoices: []
      }
      const overpaid = Math.round(this.addOverpaid() * 100) / 100
      this.calculatedOverpaidInvoices = {}
      this.checkedOverpaidInv.forEach(item => {
        this.amountDistribution(+item.credit, true)
        this.amountOverpaid(item, item.credit, true)
      })
      const paymentsArray = {
        paymentArray: [],
        date: this.payment.paymentDate
      }
      this.checkedUnderpaidInv.forEach((underpaidInv, index) => {
        let totalCredit = 0
        const length = this.checkedUnderpaidInv.length - 1
        if (overpaid) {
          const overpaidInvoices = this.generateOverpaidPayment(underpaidInv)
          this.checkedOverpaidInv.forEach(item => {
            const overpaidInvoice = {
              number: item.number,
              id: item.id,
              credit: this.roundNum(+this.calculatedOverpaidInvoices[item.id][underpaidInv.id])
            }
            totalCredit += overpaidInvoice.credit
            const key = item.isCreditNote ? 'soaCreditNotes' : 'overpaidInvoices'
            if (overpaidInvoice.credit > 0) {
              overpaidInvoices[key].push(overpaidInvoice)
            }
          })
          overpaidPaymentFull.invoices.push(overpaidInvoices)
          const payment = this.generatePayment(underpaidInv)
          payment.amountReceived -= this.roundNum(totalCredit)
          if (payment.amountReceived > 0) {
            paymentsArray.paymentArray.push(payment)
          }
          overpaidInvoices.debit = this.roundNum(totalCredit)
          const close = payment.amountReceived > 0
          if (length === index) {
            this.payOverpaidInvoices(overpaidPaymentFull, close, paymentsArray)
          }
        } else {
          const payment = this.generatePayment(underpaidInv)
          payment.amountReceived > 0 ? paymentsArray.paymentArray.push(payment) : payment.amountReceived = 0
        }
        if (length === index) {
          this.checkedOverpaidInv = []
        }
      })
      if (!overpaid) {
        this.createPayment(paymentsArray)
      }
    },
    createPayment (payment) {
      this.errorAmount = payment.paymentArray.length === 0 ? 'mandatory field' : ''
      if (this.errorAmount) return
      PaymentApi.createPayment(payment)
        .then((resp) => {
          this.loaderRun = false
          this.payment.amountReceived = 0
          this.$emit('close')
        })
        .catch((err) => {
          this.loaderRun = false
          this.errorText = _.get(err, 'response.data.errors.message', '')
          this.setCustomer({id: this.payment.customer})
          console.error(err)
        })
    },
    payOverpaidInvoices (payment, close, simplePayment) {
      this.loaderRun = true
      InvoicesApi.overpaidInvoices(payment)
        .then((resp) => {
          if (simplePayment.paymentArray.length > 0) {
            this.createPayment(simplePayment)
          } else {
            this.$emit('close')
          }
        })
        .catch((err) => {
          this.loaderRun = false
          console.error(err)
        })
    },
    clearProps () {
      this.checkedUnderpaidInv = []
      this.checkedOverpaidInv = []
      this.checkedPayments = {}
      this.calculatedOverpaidInvoices = {}
      this.totalOverpaid = 0
    },
    getUser () {
      const userId = _.get(this.userInfo, 'id', '')
      if (userId) return
      userApi.getUser()
        .then((resp) => {
          this.userInfo = resp.data
        })
        .catch((err) => {
          console.error(err)
        })
    }
  },
  beforeDestroy () {
    this.clearProps()
  },
  beforeMount () {
    this.getUser()
    this.sortContacts()
    this.getClientsSelect()
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../../sass/variables";
  @import "../../../../sass/mixins";

  .bb-payment {
    @include global-font;
    max-width: 950px;
    min-height: 615px;

    &-header {
      text-align: center;
      font-weight: 600;
      font-size: 20px;
      line-height: 24px;
      margin-bottom: 38px;
    }

    &-draft {
      color: $color-jaffa;
      padding: 4px 6px;
      border: 1px solid rgba($color-jaffa, 0.14);
      box-sizing: border-box;
      border-radius: 4px;
      margin-left: 10px;
    }

    &-info {
      display: flex;
      justify-content: space-between;

      &-form {
        width: 400px;
        margin-right: 144px;

        &-item {
          position: relative;
          display: flex;
          align-items: center;
          min-height: 30px;
          margin-bottom: 17px;

          &:last-child {
            margin-bottom: 0;
          }

          &__input {
            width: 100%;
          }

          &__select {
            font-size: 12px;
            height: 30px;
            color: $color-black;
          }
        }
      }

      &-total {
        width: 194px;
        min-height: 124px;
        color: $color-accent;
        border: 1px solid $color-wild-sand;
        box-sizing: border-box;
        border-radius: 5px;
        padding: 15px 23px;
        text-align: center;

        &__title {
          margin-bottom: 3px;
          font-weight: 300;
          font-size: 12px;
          line-height: 14px;
        }

        &__received {
          margin-bottom: 17px;
          font-weight: 600;
          font-size: 18px;
          line-height: 21px;

          span {
            font-weight: 300;
          }
        }

        &-paid {
          display: flex;
          justify-content: space-between;
          font-weight: 500;
          font-size: 12px;
          line-height: 14px;

          &__underpaid {
            color: $color-red;
            margin-bottom: 8px;
          }

          &__overpaid {
            color: $color-limeade;
          }
        }
      }
    }

    &-invoice {
      margin-top: 30px;

      &__title {
        color: $color-accent;
        font-weight: 600;
        font-size: 17px;
        line-height: 20px;
      }

      &-table-wrapper {
        position: relative;
        background: $color-white;
        box-shadow: 0 0 20px rgba($color-black, 0.05);
        border-radius: 5px;
        padding: 0 25px;
        margin: 15px 4px 12px;
        max-height: 207px;

        &_width {
          width: 150px;
        }

        &_payment {
          width: 200px;
        }
      }

      &-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        &__button {
          width: 78px;
          height: 38px;
        }

        &__error {
          margin-right: 10px;
          right: 12px;
          color: $color-red-orange;
          font-size: 11px;
          line-height: 13px;
        }
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Finance/InvoiceTable/Payment/Payment.vue